import React, { ReactNode, useState } from 'react'
import { get } from 'lodash'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Close } from '@mui/icons-material'
import { DialogProps } from '@mui/material/Dialog/Dialog'
import { StyledStack } from 'src/components/styledStack'

export interface AlertDialogProps extends Omit<DialogProps, 'title'> {
  /**
   * If true, the component is shown.
   * @default: false
   */
  open: boolean
  /**
   * The title of the alert dialog.
   */
  title?: string | ReactNode
  /**
   * The message to display in the dialog.
   * @type {string | ReactNode}
   * */
  message?: string | ReactNode
  /**
   * The message text's color.
   * @default text.secondary
   * The value of the color should be the string after the "theme.palette.",
   * eg: "error.main", "text.primary", "text.secondary" etc.
   */
  messageColor?: string
  /**
   * The confirm button text.
   * @default Yes
   */
  yesText?: string
  /**
   * The callback action when the user clicks the "Yes" button.
   */
  yesAction?: (checked?: boolean) => void
  /**
   * If `true`, the loading indicator is shown.
   * @default false
   */
  yesLoading?: boolean
  /**
   * The cancel button text.
   * @default No
   */
  noText?: string
  /**
   * The Yes button text's color.
   * @default error.main
   * The value of the color should be the string after the "theme.palette.",
   * eg: "error.main", "text.primary", "text.secondary" etc.
   */
  yesColor?: string
  /**
   * The No button text's color.
   * @default primary.main
   * The value of the color should be the string after the "theme.palette.",
   * eg: "error.main", "text.primary", "text.secondary" etc.
   */
  noColor?: string
  /**
   * The text of the checkbox. If it is not undefined, there will be "Checkbox" displayed on the bottom-left of the
   * dialog.
   * @default undefined
   */
  checkboxText?: string
  /**
   * The value of the checkbox.
   * @default false
   */
  checkboxValue?: boolean
  /**
   * The callback action when the user clicks the "Close" icon. If the onClose is not undefined, there will be a
   * "Close" icon on the top-right of the dialog.
   * @default undefined
   */
  onClose?: () => void
  /**
   * The callback action when the user clicks the "Cancel" button.
   */
  noAction?: () => void
}

/**
 * Alert Dialog component.
 */
const AlertDialog: React.FC<AlertDialogProps> = ({
  open,
  title,
  message,
  yesAction,
  noAction,
  yesLoading,
  messageColor = 'text.secondary',
  yesText = 'Yes',
  noText = 'No',
  yesColor = 'error.main',
  noColor = 'primary.main',
  checkboxText = '',
  checkboxValue: value = false,
  onClose,
  ...rest
}) => {
  const [checkboxValue, setCheckboxValue] = useState(value)
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxValue(event.target.checked)
  }
  return (
    <Dialog
      data-element="alert-dialog"
      {...rest}
      aria-describedby={rest['aria-describedby'] ?? 'alert-dialog-description'}
      aria-labelledby={rest['aria-labelledby'] ?? 'alert-dialog-title'}
      fullWidth={true}
      maxWidth={'sm'}
      onClose={noAction}
      open={open}
    >
      <DialogTitle data-element="alert-dialog-title" id="alert-dialog-title">
        <StyledStack spaceBetween row flexStart sx={{ pr: onClose ? '24px' : 0 }}>
          {title}
          {onClose && (
            <IconButton
              onClick={onClose}
              sx={{ position: 'absolute', top: '8px', right: '8px' }}
              data-element="alert-dialog-close"
            >
              <Close />
            </IconButton>
          )}
        </StyledStack>
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          component={'div'}
          data-element="alert-dialog-description"
          id="alert-dialog-description"
          sx={{ color: theme => get(theme.palette, messageColor) }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <StyledStack row sx={{ width: '100%', justifyContent: checkboxText ? 'space-between' : 'flex-end' }}>
          {checkboxText && (
            <FormControlLabel
              value="checkbox"
              sx={{ pl: 3 }}
              control={
                <Checkbox
                  color="secondary"
                  data-element="alert-dialog-checkbox"
                  checked={checkboxValue}
                  onChange={handleCheckboxChange}
                />
              }
              label={checkboxText}
              labelPlacement="end"
            />
          )}
          <Box>
            <LoadingButton
              data-element="alert-dialog-yes-button"
              loading={yesLoading}
              autoFocus
              sx={{ color: theme => get(theme.palette, yesColor) }}
              onClick={() => yesAction?.(checkboxValue)}
            >
              {yesText}
            </LoadingButton>
            <Button
              data-element="alert-dialog-no-button"
              onClick={noAction}
              sx={{ color: theme => get(theme.palette, noColor) }}
            >
              {noText}
            </Button>
          </Box>
        </StyledStack>
      </DialogActions>
    </Dialog>
  )
}

export default AlertDialog
